import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { nativeDownload } from "@/lib/mediacache";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const fmtSize = (b) => {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(2)} MB`;
};

const iconFor = (ct = "") => ct.startsWith("image/") ? "Image" : ct.includes("pdf") ? "FileText" : "File";

export default function Storage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = () => api.get("/files/mine").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const download = async (f) => {
    try {
      await nativeDownload(f.id, { inline: false });
    } catch { toast.error("Could not download file"); }
  };

  const del = async (id) => {
    await api.delete(`/files/${id}`);
    toast.success("File deleted");
    load();
  };

  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const quota = 1024 * 1024 * 1024; // 1 GB visual reference
  const pct = Math.min(100, (data.total_bytes / quota) * 100);

  return (
    <div className="p-6 sm:p-8 max-w-4xl">
      <header className="mb-6">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-400">Storage</p>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight mt-1">My Storage</h1>
        <p className="text-sm text-gray-500 mt-1">
          {user?.role === "admin" ? "All files across the vault" : "Files in your personal Drive folder"} · backend: <span className="font-medium">{data.backend === "google_drive" ? "Google Drive" : "Local disk"}</span>
        </p>
      </header>

      <div className="bg-white border border-gray-200 rounded-md p-5 mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">{fmtSize(data.total_bytes)} used</span>
          <span className="text-sm text-gray-400">{data.count} file{data.count === 1 ? "" : "s"}</span>
        </div>
        <Progress value={pct} className="h-2" />
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="hidden sm:grid grid-cols-[1fr_120px_130px_90px] gap-4 px-5 py-3 border-b border-gray-100 text-[11px] uppercase tracking-wider font-semibold text-gray-400">
          <div>File</div><div>Size</div><div>Linked to</div><div className="text-right">Actions</div>
        </div>
        {data.files.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <Icons.FolderOpen className="w-8 h-8 mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">No files uploaded yet.</p>
          </div>
        ) : data.files.map((f) => (
          <div key={f.id} className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-gray-50 sm:grid sm:grid-cols-[1fr_120px_130px_90px] sm:gap-4 sm:items-center" data-testid={`file-row-${f.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 shrink-0 rounded-md bg-gray-100 flex items-center justify-center">
                {React.createElement(Icons[iconFor(f.content_type)] || Icons.File, { className: "w-4 h-4 text-gray-600" })}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{f.original_filename}</div>
                <div className="text-[11px] text-gray-400 truncate">{fmtSize(f.size_bytes)} · {new Date(f.created_at).toLocaleDateString()}{user?.role === "admin" && f.uploader ? ` · ${f.uploader}` : ""}</div>
              </div>
            </div>
            <div className="hidden sm:block text-sm text-gray-600">{fmtSize(f.size_bytes)}</div>
            <div className="hidden sm:block text-sm text-gray-600 truncate">
              {f.linked_item ? (
                <button className="text-[#1B4332] hover:underline" onClick={() => navigate(`/item/${f.linked_item.id}`)}>{f.linked_item.title}</button>
              ) : <span className="text-gray-400">—</span>}
            </div>
            <div className="flex items-center justify-end gap-1 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => download(f)} data-testid={`download-${f.id}`}><Icons.Download className="w-4 h-4" /></Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" data-testid={`delete-file-${f.id}`}><Icons.Trash2 className="w-4 h-4" /></Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this file?</AlertDialogTitle>
                    <AlertDialogDescription>“{f.original_filename}” will be permanently removed from storage{f.linked_item ? " and unlinked from its asset" : ""}.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => del(f.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
